import { kebabCase } from 'lodash';
import {
  ComponentPropsWithRef,
  Ref,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { formatTestId } from '../../../data/utils/string.utils';
import { BaseInput, BaseInputIcon } from './base-input';
import { BasePhone } from './base-phone';

type InputFieldProps = ComponentPropsWithRef<typeof BaseInput> & {
  type?: string;
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  icon?: BaseInputIcon;
  error?: string | { error: string; [key: string]: string };
};

export function InputField({
  testId,
  className,
  label,
  description,
  prefix,
  suffix,
  icon = BaseInputIcon.NONE,
  error,
  ...props
}: InputFieldProps) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-field', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  const [type, setType] = useState<string>('text');
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    if (props.type) {
      setType(props.type);
    }
  }, [props.type]);

  useEffect(() => {
    if (props.type === 'number') {
      if (typeof props.value === 'number') {
        setValue((props.value as unknown as number)?.toString() ?? '');
      }
      if (typeof props.value === 'string') {
        const numValue =
          typeof props.value === 'string'
            ? parseFloat(props.value)
            : props.value;
        setValue(Number.isNaN(numValue) ? '' : numValue.toString());
      }
    } else {
      setValue(props.value ? props.value : '');
    }
  }, [props.type, props.value]);

  const onTogglePassword = useCallback(() => {
    setType(type === 'password' ? 'text' : 'password');
  }, [type]);

  const onClear = useCallback(() => {
    setValue('');
    props?.onChange?.('', { target: { value: '' } } as React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement
    >);
  }, [props]);

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');
      if (type === 'date') {
        const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
        if (dateRegex.test(pastedText)) {
          const [day, month, year] = pastedText.split('-');
          const formattedDate = `${year}-${month}-${day}`; // YYYY-MM-DD format
          setValue(formattedDate);
        }
        e.preventDefault();
      }
    },
    [type]
  );

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--${type}${
        className ? ` form-group--${className}` : ''
      }${error ? ' invalid' : ''}${props.disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          {description}
        </div>
      )}

      <div className="input-group">
        {prefix && (
          <span
            data-test-id={getTestId('prefix')}
            className="form-control-prefix"
          >
            {prefix}
          </span>
        )}
        {suffix && (
          <span
            data-test-id={getTestId('suffix')}
            className="form-control-suffix"
          >
            {suffix}
          </span>
        )}

        {props.type === 'password' ? (
          <i
            data-test-id={getTestId('icon')}
            className={`icon ${
              type === 'password'
                ? kebabCase(BaseInputIcon.EYE)
                : kebabCase(BaseInputIcon.EYE_OFF)
            }`}
            role="link"
            tabIndex={-1}
            onClick={() => onTogglePassword()}
            onKeyDown={makeKeyDownHandler(() => onTogglePassword())}
          />
        ) : (
          icon !== BaseInputIcon.NONE && (
            <i
              data-test-id={getTestId('icon')}
              className={`icon ${kebabCase(icon)}`}
            />
          )
        )}

        {type === 'phone' ? (
          <BasePhone
            {...props}
            testId={getTestId('input')}
            ref={(props.ref as Ref<HTMLInputElement>) ?? undefined}
          />
        ) : (
          <BaseInput
            {...props}
            value={value}
            onChange={(v, e) => {
              setValue(v);
              props?.onChange?.(v, e);
            }}
            onPaste={type === 'date' ? onPaste : undefined}
            testId={getTestId('input')}
            type={type}
            ref={(props.ref as Ref<HTMLInputElement>) ?? undefined}
          />
        )}

        {type === 'search' && value && (
          <i
            data-test-id={getTestId('icon')}
            className={`icon ${kebabCase(BaseInputIcon.CLOSE)}`}
            role="link"
            tabIndex={-1}
            onClick={() => onClear()}
            onKeyDown={makeKeyDownHandler(() => onClear())}
          />
        )}

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
